import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/lib/SessionContext';
import { useAsa } from '@/context/AsaContext';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { X, Plus, Trash2, AlertCircle, CheckCircle2, Search, Upload, Loader2, Sparkles, Droplets, Package, Banknote } from 'lucide-react';
import PicSelector from '@/components/shared/PicSelector';
import SearchableCombobox from '@/components/ui/SearchableCombobox';
import DiscoverySetItems from '../hpp/DiscoverySetItems';
import { toast } from 'sonner';

const KATEGORI_BAHAN = ['Bibit', 'Alkohol', 'Aqua Des', 'DPG', 'Peg', 'Sustain', 'Quidex', 'Castor Oil', 'Lainnya'];
const PARFAROME_GRADE = ['Deluxe', 'Premium', 'Exclusive'];
const ITEM_TAMBAHAN_KATEGORI = ['Stiker', 'Box', 'Sablon', 'Hot Print', 'Gift Card', 'Coloring', 'Lainnya'];
const emptyBahan = () => ({ kategori_bahan: 'Bibit', vendor: '', nama_bahan: '', persentase: 0, volume_ml: 0, dari_client: false });

export default function BrandProdukForm({ racikanItem, bahanItem, initialBrand, initialVarian, copyFromVariant, isMarketing, onClose, onSaved, initialDiscoverySetData = null }) {
  const qc = useQueryClient();
  const [loadingModal, setLoadingModal] = useState({ open: false, type: '', message: '' });
  const { openAsa } = useAsa();
  const isEdit = !!(racikanItem?.id || bahanItem?.id || initialDiscoverySetData?.id);
  const isAddSize = !isEdit && copyFromVariant && copyFromVariant.sizes?.length > 0;
  const sourceSize = isAddSize ? copyFromVariant.sizes[0] : null;

  // Master Data
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });
  const { data: bahanCairList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: itemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan'], queryFn: () => base44.entities.ItemTambahan.list() });
  const { data: allDatabaseBahan = [] } = useQuery({ queryKey: ['database-bahan'], queryFn: () => base44.entities.DatabaseBahan.list() });
  const { data: allRacikanDatabase = [] } = useQuery({ queryKey: ['racikan-database'], queryFn: () => base44.entities.RacikanDatabase.list() });

  const vendorOptions = [...new Set(bahanCairList.map(b => b.vendor).filter(Boolean))].sort();

  const normalize = (str) => (str || '').replace(/[^a-z0-9]/gi, '').toLowerCase();

  const mapRacikanNames = (racikanArray) => {
    if (!racikanArray || !Array.isArray(racikanArray)) return [];
    return racikanArray.map(rc => {
      let matched = bahanCairList.find(x => (x.nama || '').trim().toLowerCase() === (rc.nama_bahan || '').trim().toLowerCase());
      if (!matched) {
        const rcNorm = normalize(rc.nama_bahan);
        matched = bahanCairList.find(x => normalize(x.nama) === rcNorm);
      }
      if (!matched) {
        const std = ['dpg', 'aqua des', 'alkohol', 'peg', 'sustain', 'quidex', 'castor oil'];
        const catLower = (rc.kategori_bahan || '').trim().toLowerCase();
        if (std.includes(catLower)) {
          matched = bahanCairList.find(x => (x.kategori || '').trim().toLowerCase() === catLower);
        }
      }
      return {
        ...rc,
        nama_bahan: matched ? matched.nama : rc.nama_bahan
      };
    });
  };

  // Unified State
  const [form, setForm] = useState({
    // General
    nama_brand: initialDiscoverySetData?.nama_brand || racikanItem?.nama_brand || bahanItem?.nama_brand || initialBrand || '',
    nama_varian: initialDiscoverySetData?.nama_varian || racikanItem?.nama_varian || bahanItem?.nama_varian || initialVarian || '',
    catatan: racikanItem?.catatan || bahanItem?.catatan || (sourceSize?.racikan?.catatan || ''),
    status: racikanItem?.status || bahanItem?.status || 'aktif',
    pic: initialDiscoverySetData?.pic || racikanItem?.pic || (sourceSize?.racikan?.pic || ''),
    
    // Kemasan
    botol_nama: bahanItem?.botol_nama || (bahanItem?.is_fullset ? 'Satu set dengan botol (Fullset)' : ''),
    botol_ukuran_label_ml: bahanItem?.botol_ukuran_label_ml || racikanItem?.ukuran_ml || '',
    botol_ukuran_aktual_ml: bahanItem?.botol_ukuran_aktual_ml || '',
    acuan_volume: bahanItem?.botol_ukuran_aktual_ml ? 'aktual' : 'label', // Choose which one to use for formula calculation
    tutup_nama: bahanItem?.tutup_nama || (bahanItem?.is_fullset ? 'Satu set dengan botol (Fullset)' : ''),
    spray_nama: bahanItem?.spray_nama || (bahanItem?.is_fullset ? 'Satu set dengan botol (Fullset)' : ''),
    item_tambahan: initialDiscoverySetData?.item_tambahan || bahanItem?.item_tambahan || [],
    image_url: bahanItem?.image_url || '',

    // Formula
    racikan: mapRacikanNames(Array.isArray(racikanItem?.racikan) ? racikanItem.racikan : (isAddSize && Array.isArray(sourceSize?.racikan?.racikan) ? sourceSize.racikan.racikan : [])),

    custom_hpp: bahanItem?.custom_hpp ?? (isAddSize ? sourceSize?.bahan?.custom_hpp ?? '' : ''),
    custom_jasa: bahanItem?.custom_jasa ?? (isAddSize ? sourceSize?.bahan?.custom_jasa ?? '' : ''),
    custom_margin: bahanItem?.custom_margin ?? (isAddSize ? sourceSize?.bahan?.custom_margin ?? '' : ''),
    custom_harga_jual: bahanItem?.custom_harga_jual ?? (isAddSize ? sourceSize?.bahan?.custom_harga_jual ?? '' : ''), // Keep for backwards compatibility during load if needed, but we will use custom_margin
  });

  const [uploadingImage, setUploadingImage] = useState(false);
  const fileRef = React.useRef(null);

  const [showBotolDrop, setShowBotolDrop] = useState(false);
  const [showTutupDrop, setShowTutupDrop] = useState(false);
  const [showSprayDrop, setShowSprayDrop] = useState(false);

  const [botolSearch, setBotolSearch] = useState(bahanItem?.is_fullset ? (bahanItem?.fullset_nama || '') : (bahanItem?.botol_nama || ''));
  const [tutupSearch, setTutupSearch] = useState(bahanItem?.tutup_nama || '');
  const [spraySearch, setSpraySearch] = useState(bahanItem?.spray_nama || '');

  const filteredBotol = botolList.filter(b => b.nama?.toLowerCase().includes(botolSearch.toLowerCase()));
  const filteredTutup = tutupList.filter(t => t.nama?.toLowerCase().includes(tutupSearch.toLowerCase()));
  const filteredSpray = sprayList.filter(s => s.nama?.toLowerCase().includes(spraySearch.toLowerCase()));

  // UI Tabs for Kemasan Layout
  const [tabFullset, setTabFullset] = useState(form.botol_nama === 'Dari Client' ? 'client' : 'satala');
  const [tabBotol, setTabBotol] = useState(form.botol_nama === 'Dari Client' ? 'client' : 'satala');
  const [tabTutup, setTabTutup] = useState(form.tutup_nama === 'Dari Client' ? 'client' : 'satala');
  const [tabSpray, setTabSpray] = useState(form.spray_nama === 'Dari Client' ? 'client' : 'satala');

  // Mutations
  const createRacikan = useMutation({ mutationFn: (data) => base44.entities.DatabaseRacikan.create(data) });
  const updateRacikan = useMutation({ mutationFn: (data) => base44.entities.DatabaseRacikan.update(data.id, data) });
  const createBahan = useMutation({ mutationFn: (data) => base44.entities.DatabaseBahan.create(data) });
  const updateBahanMut = useMutation({ mutationFn: (data) => base44.entities.DatabaseBahan.update(data.id, data) });

  const [isFullset, setIsFullset] = useState(bahanItem?.is_fullset || false);
  const [isDiscoverySetMode, setIsDiscoverySetMode] = useState(!!initialDiscoverySetData);
  const [packagingIds, setPackagingIds] = useState(initialDiscoverySetData?.packaging_ids || { racikan_id: null, bahan_id: null });
  const [discoverySetItems, setDiscoverySetItems] = useState(() => {
    if (initialDiscoverySetData?.items) {
      return initialDiscoverySetData.items.map((item) => {
        const ob = item._original_bahan || {};
        const or = item._original_racikan || {};
        
        const fullsetId = item.is_fullset && ob.fullset_nama ? (fullsetList.find(x => x.nama === ob.fullset_nama)?.id || null) : null;
        const botolId = !item.is_fullset && ob.botol_nama ? (botolList.find(x => x.nama === ob.botol_nama)?.id || null) : null;
        const tutupId = !item.is_fullset && ob.tutup_nama ? (tutupList.find(x => x.nama === ob.tutup_nama)?.id || null) : null;
        const sprayId = !item.is_fullset && ob.spray_nama ? (sprayList.find(x => x.nama === ob.spray_nama)?.id || null) : null;
        
        const mappedRacikan = (or.racikan || []).map(rac => {
          let bId = null, vId = null;
          const namaBahan = rac.nama_bahan || rac.bahan_nama || '';
          const kategori = rac.kategori_bahan || rac.kategori || 'Bibit';
          
          if (namaBahan) {
            const match = bahanCairList.find(x => {
              const nameMatch = (x.nama || '').trim().toLowerCase() === namaBahan.trim().toLowerCase();
              const vendorMatch = rac.vendor ? (x.vendor || '').trim().toLowerCase() === rac.vendor.trim().toLowerCase() : true;
              return nameMatch && vendorMatch;
            });
            if (match) { bId = match.id; vId = match.vendor; }
          }
          return {
            ...rac,
            kategori: kategori,
            kategori_bahan: kategori,
            bahan_nama: namaBahan,
            nama_bahan: namaBahan,
            bahan_id: bId,
            vendor: rac.vendor || vId
          };
        });

        return {
          ...item,
          fullset_id: fullsetId,
          botol_id: botolId,
          tutup_id: tutupId,
          spray_id: sprayId,
          custom_ukuran_aktual_ml: ob.botol_ukuran_aktual_ml,
          racikan: mappedRacikan
        };
      });
    }
    return [{ racikan: [], nama_variant: '' }];
  });
  const [deletedDSItems, setDeletedDSItems] = useState([]);
  
  const handleRemoveDSItem = (item) => {
    if (item._racikan_id || item._bahan_id) {
      setDeletedDSItems(prev => [...prev, item]);
    }
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Helper: parse decimal string that may use comma (Indonesian locale) or dot
  const parseDecimal = (v) => {
    if (v === '' || v === null || v === undefined) return 0;
    return Number(String(v).replace(',', '.')) || 0;
  };
  
  const baseVolumeMl = form.acuan_volume === 'label' ? Number(form.botol_ukuran_label_ml) || 0 : (Number(form.botol_ukuran_aktual_ml) || Number(form.botol_ukuran_label_ml) || 0);

  const updateRacikanVolumes = (racikanList, baseVol) => racikanList.map(r => ({
    ...r, volume_ml: parseFloat(((parseDecimal(r.persentase) / 100) * baseVol).toFixed(2))
  }));

  const handleAcuanChange = (val) => {
    const newVal = val;
    setForm(p => ({
      ...p,
      acuan_volume: newVal,
      racikan: updateRacikanVolumes(p.racikan, newVal === 'label' ? Number(p.botol_ukuran_label_ml) : (Number(p.botol_ukuran_aktual_ml) || Number(p.botol_ukuran_label_ml)))
    }));
  };

  const handleBotolLabelChange = (val) => {
    const newVal = Number(val) || 0;
    const newBase = form.acuan_volume === 'label' ? newVal : baseVolumeMl;
    setForm(p => ({
      ...p,
      botol_ukuran_label_ml: newVal,
      racikan: form.acuan_volume === 'label' ? updateRacikanVolumes(p.racikan, newBase) : p.racikan
    }));
  };

  const handleBotolAktualChange = (val) => {
    const newVal = Number(val) || 0;
    const newBase = form.acuan_volume === 'aktual' ? newVal : baseVolumeMl;
    setForm(p => ({
      ...p,
      botol_ukuran_aktual_ml: newVal,
      racikan: form.acuan_volume === 'aktual' ? updateRacikanVolumes(p.racikan, newBase) : p.racikan
    }));
  };

  // Racikan Handlers
  const addBahan = () => setForm(p => ({ ...p, racikan: [...p.racikan, emptyBahan()] }));
  const removeBahan = (i) => setForm(p => ({ ...p, racikan: p.racikan.filter((_, idx) => idx !== i) }));
  const updateBahan = (i, k, v) => {
    setForm(p => ({
      ...p,
      racikan: p.racikan.map((r, idx) => {
        if (idx !== i) return r;
        const next = { ...r, [k]: v };
        if (k === 'kategori_bahan') {
          const catLower = (v || '').trim().toLowerCase();
          const stdCategories = ['dpg', 'aqua des', 'alkohol', 'peg', 'sustain', 'quidex', 'castor oil'];
          
          if (stdCategories.includes(catLower) || (v === 'Alkohol' && catLower === 'alkohol')) {
            const matches = bahanCairList.filter(x => (x.kategori || '').trim().toLowerCase() === catLower);
            if (matches.length === 1) {
              next.nama_bahan = matches[0].nama || '';
              next.vendor = matches[0].vendor || '';
            } else {
              next.nama_bahan = '';
              next.vendor = '';
            }
          } else if (v === 'Lainnya' || v === 'Bibit') {
            next.nama_bahan = '';
            next.vendor = '';
          }
        }
        if (k === 'persentase') {
          next.volume_ml = parseFloat(((parseDecimal(v) / 100) * baseVolumeMl).toFixed(2));
        }
        return next;
      })
    }));
  };

  // Item Tambahan Handlers
  const addItemTambahan = () => setForm(p => ({ ...p, item_tambahan: [...p.item_tambahan, { nama: '', kategori: 'Stiker', qty_per_pcs: 1, catatan: '', dari_client: false }] }));
  const removeItemTambahan = (i) => setForm(p => ({ ...p, item_tambahan: p.item_tambahan.filter((_, idx) => idx !== i) }));
  const updateItemTambahan = (i, k, v) => setForm(p => ({
    ...p, item_tambahan: p.item_tambahan.map((it, idx) => idx === i ? { ...it, [k]: v } : it)
  }));

  // Kemasan Select Handlers
  const handleSelectBotol = (b) => {
    const newBase = form.acuan_volume === 'label' ? Number(b.ukuran_label_ml) : (Number(b.ukuran_aktual_ml) || Number(b.ukuran_label_ml));
    setForm(p => ({
      ...p,
      botol_nama: b.nama,
      botol_ukuran_label_ml: Number(b.ukuran_label_ml) || '',
      botol_ukuran_aktual_ml: Number(b.ukuran_aktual_ml) || '',
      racikan: updateRacikanVolumes(p.racikan, newBase)
    }));
    setBotolSearch(b.nama);
    setShowBotolDrop(false);
  };
  const handleSelectTutup = (t) => { set('tutup_nama', t.nama); setTutupSearch(t.nama); setShowTutupDrop(false); };
  const handleSelectSpray = (s) => { set('spray_nama', s.nama); setSpraySearch(s.nama); setShowSprayDrop(false); };

  // Image Upload
  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      set('image_url', file_url);
      toast.success('Gambar Artwork berhasil diunggah.');
    } catch (err) {
      toast.error('Gagal mengunggah gambar Artwork.');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  // Validations
  const racikanArray = Array.isArray(form.racikan) ? form.racikan : [];
  const totalPersen = racikanArray.reduce((s, r) => s + parseDecimal(r.persentase), 0);
  const isFormulaValid = racikanArray.length > 0 && Math.abs(totalPersen - 100) < 0.01;
  const isKemasanValid = isFullset ? (!!botolSearch && !!form.botol_ukuran_label_ml) : (!!form.botol_nama && !!form.botol_ukuran_label_ml);
  const isGeneralValid = !!form.nama_brand.trim() && !!form.nama_varian.trim();
  
  const isFormValid = isDiscoverySetMode 
    ? (isGeneralValid && discoverySetItems.length > 0 && discoverySetItems.every(i => {
        const perc = (i.racikan || []).reduce((sum, r) => sum + (Number(r.persentase) || 0), 0);
        return Math.abs(perc - 100) < 0.01 && (i.is_fullset ? i.fullset_id : i.botol_id);
      }))
    : (isGeneralValid && isKemasanValid && isFormulaValid);

  const saveMut = useMutation({
    mutationFn: async (data) => {
      const promises = [];

      if (isDiscoverySetMode) {
        deletedDSItems.forEach(item => {
          if (item._racikan_id) promises.push(base44.entities.RacikanDatabase.delete(item._racikan_id));
          if (item._bahan_id) promises.push(base44.entities.DatabaseBahan.delete(item._bahan_id));
        });

        discoverySetItems.forEach((dsItem, idx) => {
          const itemName = `${data.nama_varian} || ${dsItem.nama_variant || 'Item ' + (idx + 1)}`;
          
          const isItemFullset = dsItem.is_fullset;
          const dsBotolNama = isItemFullset ? 'Satu set dengan botol (Fullset)' : (botolList.find(b => b.id === dsItem.botol_id)?.nama || '');
          const dsFullsetNama = isItemFullset ? (fullsetList.find(f => f.id === dsItem.fullset_id)?.nama || '') : null;
          
          let botolLabel = 0;
          if (isItemFullset) {
            const f = fullsetList.find(f => f.id === dsItem.fullset_id);
            botolLabel = f ? (parseFloat(f.nama.match(/(\d+)\s*(?:ml|g|gr|gram)/i)?.[1]) || 0) : 0;
          } else {
            botolLabel = Number(botolList.find(b => b.id === dsItem.botol_id)?.botol_ukuran_label_ml) || 0;
          }
          let botolAktual = dsItem.custom_ukuran_aktual_ml !== undefined ? Number(dsItem.custom_ukuran_aktual_ml) : botolLabel;
          
          const racikanData = {
            nama_brand: data.nama_brand,
            nama_varian: itemName,
            ukuran_ml: botolLabel,
            pic: data.pic,
            racikan: (dsItem.racikan || []).map(r => ({
              ...r,
              kategori_bahan: r.kategori || r.kategori_bahan || '',
              nama_bahan: r.bahan_nama || r.nama_bahan || '',
              volume_ml: parseFloat(((Number(r.persentase) / 100) * botolLabel).toFixed(2))
            })),
            catatan: data.catatan,
            status: data.status,
          };

          const bahanData = {
            nama_brand: data.nama_brand,
            nama_varian: itemName,
            is_fullset: isItemFullset,
            fullset_nama: dsFullsetNama,
            botol_nama: dsBotolNama,
            botol_ukuran_label_ml: botolLabel,
            botol_ukuran_aktual_ml: botolAktual,
            tutup_nama: isItemFullset ? dsBotolNama : (tutupList.find(t => t.id === dsItem.tutup_id)?.nama || ''),
            spray_nama: isItemFullset ? dsBotolNama : (sprayList.find(s => s.id === dsItem.spray_id)?.nama || ''),
            item_tambahan: [],
            image_url: data.image_url,
            catatan: data.catatan,
            status: data.status,
          };

          if (dsItem._racikan_id) {
            promises.push(base44.entities.RacikanDatabase.update(dsItem._racikan_id, racikanData));
          } else {
            promises.push(base44.entities.RacikanDatabase.create(racikanData));
          }

          if (dsItem._bahan_id) {
            promises.push(base44.entities.DatabaseBahan.update(dsItem._bahan_id, bahanData));
          } else {
            promises.push(base44.entities.DatabaseBahan.create(bahanData));
          }
        });

        // Save Packaging/Box as a distinct entry
        if (data.item_tambahan && data.item_tambahan.length > 0) {
          const packagingName = `${data.nama_varian} || Packaging / Box`;
          const pkgRacikanData = {
            nama_brand: data.nama_brand,
            nama_varian: packagingName,
            ukuran_ml: 0,
            pic: data.pic,
            racikan: [],
            catatan: 'Khusus packaging / item tambahan Discovery Set',
            status: data.status,
          };
          const pkgBahanData = {
            nama_brand: data.nama_brand,
            nama_varian: packagingName,
            is_fullset: false,
            botol_ukuran_label_ml: 0,
            botol_ukuran_aktual_ml: 0,
            item_tambahan: data.item_tambahan,
            image_url: data.image_url,
            catatan: 'Khusus packaging / item tambahan Discovery Set',
            status: data.status,
          };

          if (packagingIds.racikan_id) {
            promises.push(base44.entities.RacikanDatabase.update(packagingIds.racikan_id, pkgRacikanData));
          } else {
            promises.push(base44.entities.RacikanDatabase.create(pkgRacikanData));
          }

          if (packagingIds.bahan_id) {
            promises.push(base44.entities.DatabaseBahan.update(packagingIds.bahan_id, pkgBahanData));
          } else {
            promises.push(base44.entities.DatabaseBahan.create(pkgBahanData));
          }
        } else if (packagingIds.racikan_id || packagingIds.bahan_id) {
          // If they removed all item_tambahan but the entry previously existed, delete it
          if (packagingIds.racikan_id) promises.push(base44.entities.RacikanDatabase.delete(packagingIds.racikan_id));
          if (packagingIds.bahan_id) promises.push(base44.entities.DatabaseBahan.delete(packagingIds.bahan_id));
        }

      } else {
        // Prepare Racikan Data
        const racikanData = {
          nama_brand: data.nama_brand,
          nama_varian: data.nama_varian,
          ukuran_ml: Number(data.botol_ukuran_label_ml), // Racikan uses label ml to map to size
          pic: data.pic,
          racikan: data.racikan,
          catatan: data.catatan,
          status: data.status,
        };

        // Calculate HPP Kemasan & Racikan for calculating custom_harga_jual
        let hppKemasan = 0;
        if (isFullset) {
          const fullsetMatch = fullsetList.find(x => (x.nama || '').trim().toLowerCase() === (botolSearch || '').trim().toLowerCase());
          if (fullsetMatch) hppKemasan += (fullsetMatch.harga_jual || fullsetMatch.harga_rupiah || 0);
        } else {
          const botolMatch = botolList.find(x => (x.nama || '').trim().toLowerCase() === (data.botol_nama || '').trim().toLowerCase());
          const tutupMatch = tutupList.find(x => (x.nama || '').trim().toLowerCase() === (data.tutup_nama || '').trim().toLowerCase());
          const sprayMatch = sprayList.find(x => (x.nama || '').trim().toLowerCase() === (data.spray_nama || '').trim().toLowerCase());
          if (botolMatch) hppKemasan += (botolMatch.harga_jual || botolMatch.harga_rupiah || 0);
          if (tutupMatch) hppKemasan += (tutupMatch.harga_jual || tutupMatch.harga_rupiah || 0);
          if (sprayMatch) hppKemasan += (sprayMatch.harga_jual || sprayMatch.harga_rupiah || 0);
        }
        (data.item_tambahan || []).forEach(it => {
          const matched = itemTambahanList.find(x => (x.nama || '').trim().toLowerCase() === (it.nama || '').trim().toLowerCase());
          if (matched) hppKemasan += ((matched.harga_jual || matched.harga_rupiah || 0) * (it.qty_per_pcs || 1));
        });

        const ukuranMl = (data.acuan_volume === 'aktual' ? (Number(data.botol_ukuran_aktual_ml) || Number(data.botol_ukuran_label_ml)) : Number(data.botol_ukuran_label_ml)) || 0;
        let hppRacikan = 0;
        (data.racikan || []).forEach(r => {
          if (r.dari_client) return;
          const bahanInfo = bahanCairList.find(b => (b.nama || '').trim().toLowerCase() === (r.nama_bahan || '').trim().toLowerCase());
          if (bahanInfo && !bahanInfo.dari_client) {
            const hargaUnit = bahanInfo.harga_jual || (bahanInfo.harga_rupiah ? bahanInfo.harga_rupiah * 1.3 : 0);
            const volMl = (parseDecimal(r.persentase) / 100) * ukuranMl;
            hppRacikan += (hargaUnit * volMl);
          }
        });
        const hppRacikanFinal = hppRacikan / 1000;
        const labelMl = Number(data.botol_ukuran_label_ml) || ukuranMl;
        const defaultJasa = labelMl <= 35 ? 3000 : labelMl <= 105 ? 4000 : labelMl <= 255 ? 5000 : 6000;
        const finalJasa = (data.custom_jasa !== '' && data.custom_jasa !== null && data.custom_jasa !== undefined) ? Number(data.custom_jasa) : defaultJasa;
        const hargaPerPcs = Math.round(hppRacikanFinal + hppKemasan + finalJasa);
        
        let finalMargin = null;
        if (data.custom_margin !== '' && data.custom_margin !== null && data.custom_margin !== undefined) {
          finalMargin = Number(data.custom_margin);
        } else if (data.custom_harga_jual !== '' && data.custom_harga_jual !== null && data.custom_harga_jual !== undefined) {
          finalMargin = Math.max(0, Number(data.custom_harga_jual) - hargaPerPcs);
        }
        
        let calculatedHargaJual = null;
        if (data.custom_harga_jual !== '' && data.custom_harga_jual !== null && data.custom_harga_jual !== undefined) {
          calculatedHargaJual = Number(data.custom_harga_jual);
        } else if (finalMargin !== null) {
          calculatedHargaJual = Math.round(hargaPerPcs + finalMargin);
        }

        // Prepare Bahan Data
        const bahanData = {
          nama_brand: data.nama_brand,
          nama_varian: data.nama_varian,
          is_fullset: isFullset,
          fullset_nama: isFullset ? botolSearch : null,
          botol_nama: isFullset ? (data.botol_nama || 'Satu set dengan botol (Fullset)') : data.botol_nama,
          botol_ukuran_label_ml: Number(data.botol_ukuran_label_ml),
          botol_ukuran_aktual_ml: Number(data.botol_ukuran_aktual_ml),
          tutup_nama: isFullset ? (data.tutup_nama || 'Satu set dengan botol (Fullset)') : data.tutup_nama,
          spray_nama: isFullset ? (data.spray_nama || 'Satu set dengan botol (Fullset)') : data.spray_nama,
          item_tambahan: data.item_tambahan,
          image_url: data.image_url,
          catatan: data.catatan,
          status: data.status,
          custom_jasa: (data.custom_jasa !== '' && data.custom_jasa !== null && data.custom_jasa !== undefined) ? Number(data.custom_jasa) : null,
          custom_margin: finalMargin,
          custom_harga_jual: calculatedHargaJual,
        };

        if (racikanItem?.id) promises.push(base44.entities.RacikanDatabase.update(racikanItem.id, racikanData));
        else promises.push(base44.entities.RacikanDatabase.create(racikanData));

        if (bahanItem?.id) promises.push(base44.entities.DatabaseBahan.update(bahanItem.id, bahanData));
        else promises.push(base44.entities.DatabaseBahan.create(bahanData));

        // Jika nama varian diubah saat edit, perbarui juga seluruh ukuran varian ini di brand yang sama agar tidak terpecah menjadi varian baru
        const cleanNewVarian = (data.nama_varian || '').trim();
        const cleanOldVarian = (initialVarian || '').trim();
        const cleanBrand = (data.nama_brand || initialBrand || '').trim();

        if (isEdit && cleanOldVarian && cleanNewVarian && cleanNewVarian !== cleanOldVarian) {
          const otherBahan = (allDatabaseBahan || []).filter(b => 
            (b.nama_brand || '').trim().toLowerCase() === cleanBrand.toLowerCase() &&
            (b.nama_varian || '').trim() === cleanOldVarian &&
            b.id !== bahanItem?.id
          );
          for (const b of otherBahan) {
            promises.push(base44.entities.DatabaseBahan.update(b.id, { nama_varian: cleanNewVarian }));
          }

          const otherRacikan = (allRacikanDatabase || []).filter(r => 
            (r.nama_brand || '').trim().toLowerCase() === cleanBrand.toLowerCase() &&
            (r.nama_varian || '').trim() === cleanOldVarian &&
            r.id !== racikanItem?.id
          );
          for (const r of otherRacikan) {
            promises.push(base44.entities.RacikanDatabase.update(r.id, { nama_varian: cleanNewVarian }));
          }
        }
      }

      await Promise.all(promises);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['racikan-database'] });
      qc.invalidateQueries({ queryKey: ['database-bahan'] });
      onSaved?.();
    },
    onError: (err) => {
      console.error(err);
      toast.error('Gagal menyimpan data');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) {
      if (isDiscoverySetMode) {
        toast.error('Gagal. Pastikan formulasi setiap item genap 100% dan kemasan telah dipilih.');
      } else {
        if (!isFormulaValid) toast.error('Formula cairan harus tepat 100% dan minimal ada 1 bahan.');
        if (!isKemasanValid) toast.error('Silakan isi detail botol dan ukuran label.');
      }
      return;
    }
    saveMut.mutate(form);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-card rounded-2xl shadow-2xl w-[98vw] max-w-[1800px] my-8 border border-border">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-30 rounded-t-2xl">
          <div>
            <h3 className="text-xl font-black text-slate-800 tracking-tight">{isEdit ? 'Edit Varian Produk' : 'Tambah Produk Baru'}</h3>
            <p className="text-xs text-slate-500 mt-1">Lengkapi data botol kemasan & formula cairannya sekaligus.</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full hover:bg-rose-50 hover:text-rose-600"><X className="w-5 h-5" /></Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          
          {isEdit && form.nama_varian.includes(' || ') && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 items-start">
              <div className="mt-0.5"><div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-black text-xs">i</div></div>
              <div>
                <h5 className="text-sm font-bold text-amber-800 mb-1">Mode Edit Satuan (Discovery Set)</h5>
                <p className="text-xs text-amber-700 leading-relaxed">
                  Anda sedang mengedit <strong>satu botol spesifik</strong> dari sebuah Discovery Set. Karena setiap botol memiliki perhitungan Modal (HPP) yang berbeda-beda, sistem memisahkannya agar bisa dihitung akurat. 
                  <br className="my-1" />
                  <strong>Ingin menambah botol baru ke set ini?</strong> Silakan tutup form ini dan gunakan tombol <span className="font-bold">+ Produk / Varian Baru</span> di kanan atas halaman Database.
                </p>
              </div>
            </div>
          )}

          {/* SECTION 1: General Info */}
          <div className="space-y-4">
            <h4 className="text-sm font-black text-[#8C6208] flex items-center gap-2 uppercase tracking-wider border-b border-[#E6D5B8]/40 pb-2">
              <span className="w-5 h-5 rounded-md bg-[#8C6208] text-white flex items-center justify-center text-[10px]">1</span> Info Dasar
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-700 mb-1.5 block">Nama Brand *</label>
                <Input value={form.nama_brand} onChange={e => set('nama_brand', e.target.value)} placeholder="Misal: Cielmora" required className="h-10 rounded-xl bg-slate-50 border-slate-200 focus-visible:ring-[#8C6208]" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 mb-1.5 block">Nama Varian Produk *</label>
                <Input value={form.nama_varian} onChange={e => set('nama_varian', e.target.value)} placeholder="Misal: Sun Zest 30ml" required className="h-10 rounded-xl bg-slate-50 border-slate-200 focus-visible:ring-[#8C6208]" />
              </div>
            </div>
            
            {/* Checkbox Mode Discovery Set */}
            {!isEdit && (
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
                <input
                  type="checkbox"
                  id="mode-discovery-brand"
                  checked={isDiscoverySetMode}
                  onChange={(e) => setIsDiscoverySetMode(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-[#8C6208] focus:ring-[#8C6208]"
                />
                <label htmlFor="mode-discovery-brand" className="text-sm font-medium text-slate-700 cursor-pointer">
                  Mode Discovery Set (Banyak produk mini dalam 1 kotak)
                </label>
              </div>
            )}
          </div>

          {!isDiscoverySetMode ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* SECTION 2: Kemasan & Botol */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <h4 className="text-sm font-black text-slate-800 flex items-center gap-2 uppercase tracking-wider">
                  <span className="w-5 h-5 rounded-md bg-slate-800 text-white flex items-center justify-center text-[10px]"><Package className="w-3 h-3" /></span> Kemasan & Packaging
                </h4>
                <div className="flex bg-slate-100 p-1 rounded-lg">
                  <button type="button" onClick={() => setIsFullset(false)} className={`text-[10px] font-bold px-3 py-1 rounded-md transition-all ${!isFullset ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Satuan</button>
                  <button type="button" onClick={() => setIsFullset(true)} className={`text-[10px] font-bold px-3 py-1 rounded-md transition-all ${isFullset ? 'bg-[#8C6208] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Fullset Gabungan</button>
                </div>
              </div>

              {isFullset && (
                <div className="mb-4 bg-emerald-50/30 border border-emerald-100 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-emerald-800">Pilih Data Fullset</label>
                    <div className="flex bg-emerald-100/50 p-0.5 rounded-md">
                      <button type="button" onClick={() => { setTabFullset('satala'); setBotolSearch(''); }} className={`text-[9px] font-bold px-2 py-1 rounded-sm transition-all ${tabFullset === 'satala' ? 'bg-white text-emerald-800 shadow-sm' : 'text-emerald-600/60 hover:text-emerald-800'}`}>Satala</button>
                      <button type="button" onClick={() => { setTabFullset('client'); setBotolSearch(''); }} className={`text-[9px] font-bold px-2 py-1 rounded-sm transition-all ${tabFullset === 'client' ? 'bg-emerald-600 text-white shadow-sm' : 'text-emerald-600/60 hover:text-emerald-800'}`}>Dari Client</button>
                    </div>
                  </div>
                  
                  {tabFullset === 'client' ? (
                    <div className="space-y-3 bg-white/60 border border-emerald-200/50 rounded-xl p-3 shadow-sm">
                      <div>
                        <label className="text-[10px] text-emerald-700 font-semibold mb-1 block">Pilih dari Inventori Client:</label>
                        <SearchableCombobox
                          value={botolSearch}
                          onValueChange={v => { 
                            const f = fullsetList.find(x => x.nama === v && x.dari_client);
                            if (f) {
                              const parseUkuran = (val) => val ? parseFloat(String(val).replace(/[^\d.]/g, '')) : NaN;
                              const fLabel = parseUkuran(f.ukuran_label) || parseUkuran(f.ukuran);
                              const fAktual = parseUkuran(f.ukuran_aktual);
                              const botolId = f.botol_id || f.botol?.id;
                              const b = botolList.find(x => x.id === botolId);
                              const botolNama = b?.nama || f.botol?.nama || 'Satu set dengan botol (Fullset)';
                              const labelMl = !isNaN(fLabel) ? fLabel : (Number(b?.ukuran_label_ml) || Number(f.botol?.ukuran_label_ml) || '');
                              const aktualMl = !isNaN(fAktual) ? fAktual : (!isNaN(fLabel) ? fLabel : (Number(b?.ukuran_aktual_ml) || Number(f.botol?.ukuran_aktual_ml) || ''));
                              const newBase = form.acuan_volume === 'label' ? Number(labelMl) : (Number(aktualMl) || Number(labelMl));

                              setForm(p => ({
                                ...p,
                                botol_nama: botolNama,
                                tutup_nama: b?.tutup?.nama || f.tutup?.nama || 'Satu set dengan botol (Fullset)',
                                spray_nama: b?.spray?.nama || f.spray?.nama || 'Satu set dengan botol (Fullset)',
                                botol_ukuran_label_ml: labelMl,
                                botol_ukuran_aktual_ml: aktualMl,
                                racikan: updateRacikanVolumes(p.racikan, newBase)
                              }));
                              setTabBotol('client');
                              setTabTutup('client');
                              setTabSpray('client');
                            } else {
                              // Custom input fullset from client
                              set('botol_nama', 'Dari Client');
                              set('tutup_nama', 'Dari Client');
                              set('spray_nama', 'Dari Client');
                              setTabBotol('client');
                              setTabTutup('client');
                              setTabSpray('client');
                            }
                            setBotolSearch(v);
                          }}
                          options={fullsetList.filter(f => f.dari_client).map(f => ({ value: f.nama, label: f.nama }))}
                          placeholder="Pilih dari inventori..."
                          size="sm"
                          allowCustom={true}
                        />
                      </div>
                      <div className="relative hidden">
                        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-emerald-100/60" /></div>
                        <div className="relative flex justify-center text-[9px] uppercase tracking-widest"><span className="bg-emerald-50 px-2 text-emerald-600/60 font-bold">Atau Input Baru</span></div>
                      </div>
                      <div className="hidden">
                        <Input 
                          placeholder="Fullset Dari Client" 
                          value={botolSearch}
                          onChange={e => { 
                            setBotolSearch(e.target.value); 
                            set('botol_nama', 'Dari Client');
                            set('tutup_nama', 'Dari Client');
                            set('spray_nama', 'Dari Client');
                            setTabBotol('client');
                            setTabTutup('client');
                            setTabSpray('client');
                          }}
                          className="h-8 text-xs border-emerald-200"
                        />
                      </div>
                    </div>
                  ) : (
                    <SearchableCombobox
                      value={botolSearch}
                      onValueChange={v => { 
                        const f = fullsetList.find(x => x.nama === v && !x.dari_client);
                        if (f) {
                          const parseUkuran = (val) => val ? parseFloat(String(val).replace(/[^\d.]/g, '')) : NaN;
                          const fLabel = parseUkuran(f.ukuran_label) || parseUkuran(f.ukuran);
                          const fAktual = parseUkuran(f.ukuran_aktual);
                          const botolId = f.botol_id || f.botol?.id;
                          const b = botolList.find(x => x.id === botolId);
                          const botolNama = b?.nama || f.botol?.nama || 'Satu set dengan botol (Fullset)';
                          const labelMl = !isNaN(fLabel) ? fLabel : (Number(b?.ukuran_label_ml) || Number(f.botol?.ukuran_label_ml) || '');
                          const aktualMl = !isNaN(fAktual) ? fAktual : (!isNaN(fLabel) ? fLabel : (Number(b?.ukuran_aktual_ml) || Number(f.botol?.ukuran_aktual_ml) || ''));
                          const newBase = form.acuan_volume === 'label' ? Number(labelMl) : (Number(aktualMl) || Number(labelMl));

                          setForm(p => ({
                            ...p,
                            botol_nama: botolNama,
                            tutup_nama: b?.tutup?.nama || f.tutup?.nama || 'Satu set dengan botol (Fullset)',
                            spray_nama: b?.spray?.nama || f.spray?.nama || 'Satu set dengan botol (Fullset)',
                            botol_ukuran_label_ml: labelMl,
                            botol_ukuran_aktual_ml: aktualMl,
                            racikan: updateRacikanVolumes(p.racikan, newBase)
                          }));
                          setTabBotol('satala');
                          setTabTutup('satala');
                          setTabSpray('satala');
                        }
                        setBotolSearch(v);
                      }}
                      options={fullsetList.filter(f => !f.dari_client).map(f => ({ value: f.nama, label: f.nama }))}
                      placeholder="Cari fullset Satala..."
                      size="md"
                    />
                  )}
                </div>
              )}
              
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-xl border ${isFullset ? 'border-slate-100 bg-slate-50/40 opacity-60 pointer-events-none' : 'border-slate-200 bg-slate-50/70'}`}>
                
                {/* Botol */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-slate-800">Botol / Wadah *</label>
                    <div className="flex bg-slate-200 p-0.5 rounded-md">
                      <button type="button" onClick={() => { setTabBotol('satala'); set('botol_nama', ''); }} className={`text-[9px] font-bold px-2 py-1 rounded-sm transition-all ${tabBotol === 'satala' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Satala</button>
                      <button type="button" onClick={() => { setTabBotol('client'); set('botol_nama', 'Dari Client'); setShowBotolDrop(false); }} className={`text-[9px] font-bold px-2 py-1 rounded-sm transition-all ${tabBotol === 'client' ? 'bg-emerald-50 text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Dari Client</button>
                    </div>
                  </div>
                  
                  {tabBotol === 'client' ? (
                    <div className="space-y-3 bg-gradient-to-r from-emerald-50/50 to-white border border-emerald-100 rounded-xl p-3 shadow-sm">
                      <div>
                        <label className="text-[10px] text-emerald-700 font-semibold mb-1 block">Pilih dari Inventori Client:</label>
                        <SearchableCombobox
                          value={botolSearch}
                          onValueChange={v => {
                            const b = botolList.find(x => x.nama === v && x.dari_client);
                            if (b) {
                              handleSelectBotol(b);
                            } else {
                              setBotolSearch(v);
                              set('botol_nama', v);
                            }
                          }}
                          options={botolList.filter(b => b.dari_client).map(b => ({ value: b.nama, label: b.nama }))}
                          placeholder="Pilih dari inventori..."
                          size="sm"
                          disabled={isFullset}
                        />
                      </div>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-emerald-100/60" /></div>
                        <div className="relative flex justify-center text-[9px] uppercase tracking-widest"><span className="bg-white px-2 text-emerald-600/60 font-bold">Atau Input Baru</span></div>
                      </div>
                      <div>
                        <Input 
                          placeholder="Botol Dari Client" 
                          value={form.botol_nama === 'Dari Client' ? '' : (botolSearch || form.botol_nama)}
                          onChange={e => { setBotolSearch(e.target.value); set('botol_nama', e.target.value || 'Dari Client'); }}
                          disabled={isFullset}
                          className="h-8 text-xs border-emerald-200"
                        />
                        <span className="text-[9px] text-emerald-600 font-bold mt-1 block">Tercatat sebagai: Item dari Client</span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 relative">
                      <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                      <Input
                        className="pl-9 h-9 text-xs rounded-xl bg-white border-slate-200"
                        placeholder="Cari botol inventori Satala..."
                        value={botolSearch || form.botol_nama}
                        onChange={e => { setBotolSearch(e.target.value); set('botol_nama', e.target.value); setShowBotolDrop(true); }}
                        onFocus={() => setShowBotolDrop(true)}
                        disabled={isFullset}
                      />
                      {showBotolDrop && filteredBotol.length > 0 && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowBotolDrop(false)} />
                          <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                            {filteredBotol.map(b => (
                              <button key={b.id} type="button" onClick={() => handleSelectBotol(b)} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0">
                                <div className="font-semibold text-slate-700">{b.nama}</div>
                                <div className="text-[10px] text-slate-400">{b.ukuran_label_ml}ml</div>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 mb-0.5 block">Label (ml)</label>
                      <Input type="number" required value={form.botol_ukuran_label_ml} onChange={e => handleBotolLabelChange(e.target.value)} placeholder="30" className="h-8 text-xs" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 mb-0.5 block">Aktual (ml)</label>
                      <Input type="number" value={form.botol_ukuran_aktual_ml} onChange={e => handleBotolAktualChange(e.target.value)} placeholder="33" className="h-8 text-xs" />
                    </div>
                  </div>
                </div>

                {/* Tutup */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-slate-800">Tutup (Cap)</label>
                    <div className="flex bg-slate-200 p-0.5 rounded-md">
                      <button type="button" onClick={() => { setTabTutup('satala'); set('tutup_nama', ''); }} className={`text-[9px] font-bold px-2 py-1 rounded-sm transition-all ${tabTutup === 'satala' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Satala</button>
                      <button type="button" onClick={() => { setTabTutup('client'); set('tutup_nama', 'Dari Client'); setShowTutupDrop(false); }} className={`text-[9px] font-bold px-2 py-1 rounded-sm transition-all ${tabTutup === 'client' ? 'bg-emerald-50 text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Dari Client</button>
                    </div>
                  </div>
                  
                  {tabTutup === 'client' ? (
                    <div className="space-y-3 bg-gradient-to-r from-emerald-50/50 to-white border border-emerald-100 rounded-xl p-3 shadow-sm">
                      <div>
                        <label className="text-[10px] text-emerald-700 font-semibold mb-1 block">Pilih dari Inventori Client:</label>
                        <SearchableCombobox
                          value={tutupSearch}
                          onValueChange={v => { setTutupSearch(v); set('tutup_nama', v); }}
                          options={tutupList.filter(t => t.dari_client).map(t => ({ value: t.nama, label: t.nama }))}
                          placeholder="Pilih dari inventori..."
                          size="sm"
                          disabled={isFullset}
                        />
                      </div>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-emerald-100/60" /></div>
                        <div className="relative flex justify-center text-[9px] uppercase tracking-widest"><span className="bg-white px-2 text-emerald-600/60 font-bold">Atau Input Baru</span></div>
                      </div>
                      <div>
                        <Input 
                          placeholder="Tutup Dari Client" 
                          value={form.tutup_nama === 'Dari Client' ? '' : (tutupSearch || form.tutup_nama)}
                          onChange={e => { setTutupSearch(e.target.value); set('tutup_nama', e.target.value || 'Dari Client'); }}
                          disabled={isFullset}
                          className="h-8 text-xs border-emerald-200"
                        />
                        <span className="text-[9px] text-emerald-600 font-bold mt-1 block">Tercatat sebagai: Item dari Client</span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 relative">
                      <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                      <Input
                        className="pl-9 h-9 text-xs rounded-xl bg-white border-slate-200"
                        placeholder="Cari tutup inventori Satala..."
                        value={tutupSearch || form.tutup_nama}
                        onChange={e => { setTutupSearch(e.target.value); set('tutup_nama', e.target.value); setShowTutupDrop(true); }}
                        onFocus={() => setShowTutupDrop(true)}
                        disabled={isFullset}
                      />
                      {showTutupDrop && filteredTutup.length > 0 && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowTutupDrop(false)} />
                          <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                            {filteredTutup.map(t => (
                              <button key={t.id} type="button" onClick={() => handleSelectTutup(t)} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0">
                                <div className="font-semibold text-slate-700">{t.nama}</div>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Spray */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-slate-800">Spray / Pump</label>
                    <div className="flex bg-slate-200 p-0.5 rounded-md">
                      <button type="button" onClick={() => { setTabSpray('satala'); set('spray_nama', ''); }} className={`text-[9px] font-bold px-2 py-1 rounded-sm transition-all ${tabSpray === 'satala' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Satala</button>
                      <button type="button" onClick={() => { setTabSpray('client'); set('spray_nama', 'Dari Client'); setShowSprayDrop(false); }} className={`text-[9px] font-bold px-2 py-1 rounded-sm transition-all ${tabSpray === 'client' ? 'bg-emerald-50 text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Dari Client</button>
                    </div>
                  </div>
                  
                  {tabSpray === 'client' ? (
                    <div className="space-y-3 bg-gradient-to-r from-emerald-50/50 to-white border border-emerald-100 rounded-xl p-3 shadow-sm">
                      <div>
                        <label className="text-[10px] text-emerald-700 font-semibold mb-1 block">Pilih dari Inventori Client:</label>
                        <SearchableCombobox
                          value={spraySearch}
                          onValueChange={v => { setSpraySearch(v); set('spray_nama', v); }}
                          options={sprayList.filter(s => s.dari_client).map(s => ({ value: s.nama, label: s.nama }))}
                          placeholder="Pilih dari inventori..."
                          size="sm"
                          disabled={isFullset}
                        />
                      </div>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-emerald-100/60" /></div>
                        <div className="relative flex justify-center text-[9px] uppercase tracking-widest"><span className="bg-white px-2 text-emerald-600/60 font-bold">Atau Input Baru</span></div>
                      </div>
                      <div>
                        <Input 
                          placeholder="Spray Dari Client" 
                          value={form.spray_nama === 'Dari Client' ? '' : (spraySearch || form.spray_nama)}
                          onChange={e => { setSpraySearch(e.target.value); set('spray_nama', e.target.value || 'Dari Client'); }}
                          disabled={isFullset}
                          className="h-8 text-xs border-emerald-200"
                        />
                        <span className="text-[9px] text-emerald-600 font-bold mt-1 block">Tercatat sebagai: Item dari Client</span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 relative">
                      <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                      <Input
                        className="pl-9 h-9 text-xs rounded-xl bg-white border-slate-200"
                        placeholder="Cari spray inventori Satala..."
                        value={spraySearch || form.spray_nama}
                        onChange={e => { setSpraySearch(e.target.value); set('spray_nama', e.target.value); setShowSprayDrop(true); }}
                        onFocus={() => setShowSprayDrop(true)}
                        disabled={isFullset}
                      />
                      {showSprayDrop && filteredSpray.length > 0 && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowSprayDrop(false)} />
                          <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                            {filteredSpray.map(s => (
                              <button key={s.id} type="button" onClick={() => handleSelectSpray(s)} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0">
                                <div className="font-semibold text-slate-700">{s.nama}</div>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Item Tambahan */}
              <div className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-xs font-bold text-slate-700">Item Tambahan Kemasan</h5>
                  <Button type="button" size="sm" variant="outline" onClick={addItemTambahan} className="h-6 text-[10px] gap-1 px-2 rounded">
                    <Plus className="w-3 h-3" /> Tambah
                  </Button>
                </div>
                {form.item_tambahan.length === 0 ? (
                  <p className="text-[10px] text-slate-400 italic text-center py-2">Tidak ada (Box, stiker, dsb)</p>
                ) : (
                  <div className="space-y-3">
                    {form.item_tambahan.map((it, i) => (
                      <div key={i} className="bg-slate-50 rounded-xl border border-slate-200 p-3 relative">
                        <Button type="button" variant="ghost" size="icon" className="absolute top-2 right-2 w-6 h-6 text-rose-500/50 hover:text-rose-600 hover:bg-rose-50" onClick={() => removeItemTambahan(i)}><Trash2 className="w-3 h-3" /></Button>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] font-bold text-slate-500 uppercase">Item {i + 1}</span>
                          <button type="button" onClick={() => updateItemTambahan(i, 'dari_client', !it.dari_client)} className={`text-[9px] px-2 py-0.5 rounded-full font-bold transition-all ${it.dari_client ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-slate-200 text-slate-500 hover:bg-slate-300'}`}>
                            {it.dari_client ? '✓ Dari Client' : 'Milik Sendiri / Beli'}
                          </button>
                        </div>
                        <div className="mb-2">
                          <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Kategori Item</label>
                          <div className="flex gap-1.5 flex-wrap">
                            {ITEM_TAMBAHAN_KATEGORI.map(k => {
                               const activeClass = k === 'Stiker' ? 'bg-blue-500 text-white' : 
                                                   k === 'Box' ? 'bg-orange-500 text-white' :
                                                   k === 'Sablon' ? 'bg-purple-500 text-white' :
                                                   k === 'Hot Print' ? 'bg-rose-500 text-white' :
                                                   k === 'Gift Card' ? 'bg-pink-500 text-white' :
                                                   k === 'Coloring' ? 'bg-teal-500 text-white' :
                                                   'bg-slate-700 text-white';
                               return (
                                <button key={k} type="button" onClick={() => { updateItemTambahan(i, 'kategori', k); updateItemTambahan(i, 'nama', ''); }}
                                  className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all ${(it.kategori || 'Stiker') === k ? `${activeClass} shadow-sm` : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                                  {k}
                                </button>
                               );
                            })}
                          </div>
                        </div>
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-12 sm:col-span-6">
                            <SearchableCombobox
                              value={it.nama}
                              onValueChange={v => {
                                updateItemTambahan(i, 'nama', v);
                                const found = itemTambahanList.find(x => x.nama === v);
                                if (found && found.kategori) updateItemTambahan(i, 'kategori', found.kategori);
                              }}
                              options={itemTambahanList
                                .filter(x => (it.kategori ? x.kategori === it.kategori : true))
                                .map(x => ({ value: x.nama, label: x.nama }))}
                              placeholder="Nama Item..."
                              size="sm"
                            />
                          </div>
                          <div className="col-span-5 sm:col-span-3">
                            <div className="flex items-center gap-2">
                              <label className="text-[9px] font-bold text-slate-400 uppercase">Qty:</label>
                              <Input type="number" min="1" value={it.qty_per_pcs} onChange={e => updateItemTambahan(i, 'qty_per_pcs', Number(e.target.value))} className="h-8 text-[10px] text-center px-1" />
                            </div>
                          </div>
                          <div className="col-span-7 sm:col-span-3">
                            <Input value={it.catatan || ''} onChange={e => updateItemTambahan(i, 'catatan', e.target.value)} placeholder="Ket..." className="h-8 text-[10px] px-2" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* SECTION 3: Formula Cairan */}
            <div className="space-y-4">
              <h4 className="text-sm font-black text-amber-600 flex items-center gap-2 uppercase tracking-wider border-b border-amber-200/50 pb-2">
                <span className="w-5 h-5 rounded-md bg-amber-600 text-white flex items-center justify-center text-[10px]"><Droplets className="w-3 h-3" /></span> Formula Cairan
              </h4>

              <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <label className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1 block">Gunakan Base Volume (ml)</label>
                    <div className="flex bg-white rounded-lg border border-amber-200 p-0.5 shadow-sm">
                      <button type="button" onClick={() => handleAcuanChange('label')} className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${form.acuan_volume === 'label' ? 'bg-amber-100 text-amber-800' : 'text-slate-500 hover:text-slate-700'}`}>Label ({form.botol_ukuran_label_ml || 0})</button>
                      <button type="button" onClick={() => handleAcuanChange('aktual')} className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${form.acuan_volume === 'aktual' ? 'bg-amber-100 text-amber-800' : 'text-slate-500 hover:text-slate-700'}`}>Aktual ({form.botol_ukuran_aktual_ml || 0})</button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-slate-400 mb-0.5">Total Persentase</p>
                    <div className={`text-sm font-black flex items-center justify-end gap-1 ${isFormulaValid ? 'text-emerald-600' : totalPersen > 100 ? 'text-rose-600' : 'text-amber-600'}`}>
                      {totalPersen.toFixed(1)}% {isFormulaValid && <CheckCircle2 className="w-4 h-4" />}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {racikanArray.map((row, i) => (
                    <div key={i} className="bg-white rounded-xl border border-amber-200/60 p-3 shadow-xs relative">
                      <Button type="button" variant="ghost" size="icon" className="absolute top-2 right-2 w-6 h-6 text-rose-500/50 hover:text-rose-600 hover:bg-rose-50" onClick={() => removeBahan(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      
                      <div className="flex items-center justify-between mb-3 pr-6">
                        <span className="text-[10px] font-bold text-amber-700 uppercase">Bahan {i + 1}</span>
                        <button 
                          type="button" 
                          onClick={() => {
                            updateBahan(i, 'dari_client', !row.dari_client);
                            updateBahan(i, 'vendor', '');
                            updateBahan(i, 'grade', '');
                            updateBahan(i, 'nama_bahan', '');
                          }} 
                          className={`text-[9px] px-2.5 py-0.5 rounded-full font-black uppercase tracking-wider transition-all ${
                            row.dari_client 
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' 
                              : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
                          }`}
                        >
                          {row.dari_client ? '✓ Dari Client' : 'Satala'}
                        </button>
                      </div>

                      <div className="pr-8 mb-2">
                        <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Kategori Bahan</label>
                        <div className="flex gap-1.5 flex-wrap">
                          {KATEGORI_BAHAN.map(k => (
                            <button key={k} type="button" onClick={() => updateBahan(i, 'kategori_bahan', k)}
                              className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all ${row.kategori_bahan === k ? 'bg-amber-500 text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                              {k}
                            </button>
                          ))}
                        </div>
                      </div>
 
                      <div className="grid grid-cols-12 gap-2 items-end">
                        {row.kategori_bahan !== 'Lainnya' && !row.dari_client && (
                          <div className="col-span-12 sm:col-span-5">
                            <label className="text-[10px] text-slate-500 font-bold mb-0.5 block">Vendor</label>
                            <SearchableCombobox
                              value={row.vendor}
                              onValueChange={v => { updateBahan(i, 'vendor', v); updateBahan(i, 'nama_bahan', ''); updateBahan(i, 'grade', ''); }}
                              options={(() => {
                                if (row.kategori_bahan === 'Bibit') return vendorOptions;
                                const cats = bahanCairList.filter(b => b.kategori === row.kategori_bahan && b.vendor);
                                const uniqueVendors = [...new Set(cats.map(c => c.vendor))];
                                return uniqueVendors.map(v => ({ value: v, label: v }));
                              })()}
                              placeholder="Vendor..."
                              size="sm"
                            />
                          </div>
                        )}
                        {/* Grade selector for Parfarome */}
                        {row.kategori_bahan === 'Bibit' && !row.dari_client && row.vendor && row.vendor.toLowerCase().includes('parfarome') && (
                          <div className="col-span-12">
                            <label className="text-[10px] text-slate-500 font-bold mb-0.5 block">Grade Parfarome</label>
                            <div className="flex gap-1.5 flex-wrap">
                              {PARFAROME_GRADE.map(g => (
                                <button key={g} type="button" onClick={() => { updateBahan(i, 'grade', g); updateBahan(i, 'nama_bahan', ''); }}
                                  className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all ${row.grade === g ? 'bg-amber-500 text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>{g}</button>
                              ))}
                            </div>
                            {!row.grade && <p className="text-[9px] text-amber-600 mt-0.5">⚠ Pilih grade dulu untuk melihat daftar bibit</p>}
                          </div>
                        )}
                        <div className={row.kategori_bahan === 'Bibit' && !row.dari_client ? 'col-span-12 sm:col-span-7' : 'col-span-12 sm:col-span-7'}>
                          <label className="text-[10px] text-slate-500 font-bold mb-0.5 block">Nama Bahan *</label>
                          {row.kategori_bahan === 'Lainnya' ? (
                            <Input value={row.nama_bahan} onChange={e => updateBahan(i, 'nama_bahan', e.target.value)} placeholder="Nama bahan" className="h-8 text-xs" />
                          ) : (
                            <SearchableCombobox
                              value={row.nama_bahan}
                              onValueChange={v => updateBahan(i, 'nama_bahan', v)}
                              options={(() => {
                                const filtered = bahanCairList.filter(b => {
                                  if (b.kategori !== row.kategori_bahan) return false;
                                  if (row.dari_client) {
                                    return b.dari_client === true;
                                  } else {
                                    if (b.dari_client) return false;
                                    if (row.vendor && b.vendor !== row.vendor) return false;
                                    if (row.kategori_bahan === 'Bibit' && row.vendor && row.vendor.toLowerCase().includes('parfarome') && row.grade) {
                                      return (b.grade || '').toLowerCase() === row.grade.toLowerCase();
                                    }
                                    return true;
                                  }
                                });

                                // Smart sorting: match client_owner with form.nama_brand
                                if (row.dari_client && form.nama_brand) {
                                  const currentBrandLower = form.nama_brand.toLowerCase().trim();
                                  filtered.sort((a, b) => {
                                    const aOwner = (a.client_owner || '').toLowerCase().trim();
                                    const bOwner = (b.client_owner || '').toLowerCase().trim();

                                    const aMatches = aOwner === currentBrandLower;
                                    const bMatches = bOwner === currentBrandLower;

                                    if (aMatches && !bMatches) return -1;
                                    if (!aMatches && bMatches) return 1;

                                    if (aOwner !== bOwner) return aOwner.localeCompare(bOwner, 'id');
                                    return (a.nama || '').localeCompare(b.nama || '', 'id');
                                  });
                                } else {
                                  filtered.sort((a, b) => (a.nama || '').localeCompare(b.nama || '', 'id'));
                                }

                                return filtered.map(b => ({
                                  value: b.nama,
                                  label: b.client_owner && row.dari_client
                                    ? `${b.nama} (Titipan: ${b.client_owner})`
                                    : b.nama
                                }));
                              })()}
                              placeholder={row.dari_client ? "Pilih dari inventori client..." : "Pilih Bahan..."}
                              disabled={!row.dari_client && row.kategori_bahan !== 'Lainnya' && (!row.vendor || (row.kategori_bahan === 'Bibit' && row.vendor.toLowerCase().includes('parfarome') && !row.grade))}
                              size="sm"
                              allowCustom={row.dari_client}
                            />
                          )}
                        </div>

                        <div className="col-span-6 sm:col-span-2">
                          <label className="text-[10px] text-slate-500 font-bold mb-0.5 block">%</label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={row.persentase === 0 ? '' : row.persentase}
                            onChange={e => updateBahan(i, 'persentase', e.target.value)}
                            className="h-8 text-xs font-black text-center"
                          />
                        </div>
                        <div className="col-span-6 sm:col-span-3">
                          <label className="text-[10px] text-slate-500 font-bold mb-0.5 block">Vol (ml)</label>
                          <div className="h-8 bg-slate-50 border border-slate-200 rounded-md flex items-center justify-center text-xs font-black text-amber-700">
                            {((parseDecimal(row.persentase) / 100) * baseVolumeMl).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button type="button" onClick={addBahan} variant="outline" className="w-full border-dashed border-amber-300 text-amber-700 hover:bg-amber-50 text-xs font-bold gap-2">
                    <Plus className="w-4 h-4" /> Tambah Bahan Cairan
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {/* SECTION: Manajemen Harga & Jasa */}
          {isMarketing && (() => {
            // Helper functions
            const getHargaBeli = (item) => item?.harga_rupiah || 0;
            const getHargaJual = (item) => item?.harga_jual || (item?.harga_rupiah ? item.harga_rupiah * 1.3 : 0);
            
            const ukuranMl = baseVolumeMl > 0 ? baseVolumeMl : (Number(form.botol_ukuran_label_ml) || 0);

            // 1. MODAL BELI PABRIK
            let modalKemasanUtama = 0;
            if (isFullset) {
              const fullsetMatch = fullsetList.find(x => (x.nama || '').trim().toLowerCase() === (botolSearch || '').trim().toLowerCase());
              if (fullsetMatch) modalKemasanUtama += getHargaBeli(fullsetMatch);
            } else {
              const botolMatch = botolList.find(x => (x.nama || '').trim().toLowerCase() === (form.botol_nama || '').trim().toLowerCase());
              const tutupMatch = tutupList.find(x => (x.nama || '').trim().toLowerCase() === (form.tutup_nama || '').trim().toLowerCase());
              const sprayMatch = sprayList.find(x => (x.nama || '').trim().toLowerCase() === (form.spray_nama || '').trim().toLowerCase());
              if (botolMatch) modalKemasanUtama += getHargaBeli(botolMatch);
              if (tutupMatch) modalKemasanUtama += getHargaBeli(tutupMatch);
              if (sprayMatch) modalKemasanUtama += getHargaBeli(sprayMatch);
            }

            let modalItemTambahan = 0;
            (form.item_tambahan || []).forEach(it => {
              const matched = itemTambahanList.find(x => (x.nama || '').trim().toLowerCase() === (it.nama || '').trim().toLowerCase());
              if (matched) modalItemTambahan += (getHargaBeli(matched) * (it.qty_per_pcs || 1));
            });
            const modalKemasanTotal = modalKemasanUtama + modalItemTambahan;

            let modalRacikan = 0;
            racikanArray.forEach(r => {
              if (r.dari_client) return;
              const bahanInfo = bahanCairList.find(b => (b.nama || '').trim().toLowerCase() === (r.nama_bahan || '').trim().toLowerCase());
              if (bahanInfo && !bahanInfo.dari_client) {
                const hargaUnit = getHargaBeli(bahanInfo);
                const volMl = (parseDecimal(r.persentase) / 100) * ukuranMl;
                modalRacikan += (hargaUnit * volMl);
              }
            });
            const modalRacikanFinal = modalRacikan / 1000;
            const modalFisikTotal = modalKemasanTotal + modalRacikanFinal;

            // 2. HARGA JUAL STANDAR SATALA
            let hppKemasanUtama = 0;
            if (isFullset) {
              const fullsetMatch = fullsetList.find(x => (x.nama || '').trim().toLowerCase() === (botolSearch || '').trim().toLowerCase());
              if (fullsetMatch) hppKemasanUtama += getHargaJual(fullsetMatch);
            } else {
              const botolMatch = botolList.find(x => (x.nama || '').trim().toLowerCase() === (form.botol_nama || '').trim().toLowerCase());
              const tutupMatch = tutupList.find(x => (x.nama || '').trim().toLowerCase() === (form.tutup_nama || '').trim().toLowerCase());
              const sprayMatch = sprayList.find(x => (x.nama || '').trim().toLowerCase() === (form.spray_nama || '').trim().toLowerCase());
              if (botolMatch) hppKemasanUtama += getHargaJual(botolMatch);
              if (tutupMatch) hppKemasanUtama += getHargaJual(tutupMatch);
              if (sprayMatch) hppKemasanUtama += getHargaJual(sprayMatch);
            }

            let hppItemTambahan = 0;
            (form.item_tambahan || []).forEach(it => {
              const matched = itemTambahanList.find(x => (x.nama || '').trim().toLowerCase() === (it.nama || '').trim().toLowerCase());
              if (matched) hppItemTambahan += (getHargaJual(matched) * (it.qty_per_pcs || 1));
            });
            const hppKemasan = hppKemasanUtama + hppItemTambahan;

            let hppRacikan = 0;
            racikanArray.forEach(r => {
              if (r.dari_client) return;
              const bahanInfo = bahanCairList.find(b => (b.nama || '').trim().toLowerCase() === (r.nama_bahan || '').trim().toLowerCase());
              if (bahanInfo && !bahanInfo.dari_client) {
                const hargaUnit = getHargaJual(bahanInfo);
                const volMl = (parseDecimal(r.persentase) / 100) * ukuranMl;
                hppRacikan += (hargaUnit * volMl);
              }
            });
            const hppRacikanFinal = hppRacikan / 1000;

            // 3. JASA PRODUKSI & HARGA SISTEM
            const labelMl = Number(form.botol_ukuran_label_ml) || (baseVolumeMl > 0 ? baseVolumeMl : 0);
            const defaultJasaVal = labelMl <= 35 ? 3000 : labelMl <= 105 ? 4000 : labelMl <= 255 ? 5000 : 6000;
            const jasaFinal = form.custom_jasa ? Number(form.custom_jasa) : defaultJasaVal;

            // Harga Sistem Sebelum Jasa (Murni Jual Bahan)
            const hargaSistemBahan = hppRacikanFinal + hppKemasan;
            // Total Harga Sistem + Jasa
            const totalSetelahJasa = hargaSistemBahan + jasaFinal;
            
            // 4. MARGIN & HARGA JUAL AKHIR
            let displayMargin = '';
            let displayHargaJual = '';

            if (form.custom_harga_jual !== '' && form.custom_harga_jual !== null && form.custom_harga_jual !== undefined) {
              displayHargaJual = form.custom_harga_jual;
              displayMargin = Number(form.custom_harga_jual) - Math.round(totalSetelahJasa);
            } else if (form.custom_margin !== '' && form.custom_margin !== null && form.custom_margin !== undefined) {
              displayMargin = Number(form.custom_margin);
              displayHargaJual = Math.round(totalSetelahJasa + displayMargin);
            }

            const currentMarginNum = displayMargin !== '' ? Number(displayMargin) : 0;
            const currentHargaJualNum = displayHargaJual !== '' ? Number(displayHargaJual) : Math.round(totalSetelahJasa);

            // Selisih Margin Komponen Standar
            const marginKemasan = Math.max(0, hppKemasanUtama - modalKemasanUtama);
            const marginCairan = Math.max(0, hppRacikanFinal - modalRacikanFinal);
            const marginTambahan = Math.max(0, hppItemTambahan - modalItemTambahan);
            
            // Keuntungan Bersih Riil: Harga Jual Aktual dikurangi Modal Pokok Fisik
            const totalProfitBersih = currentHargaJualNum - modalFisikTotal;
            const isBoncos = totalProfitBersih < 0;
            const isUnderSystem = !isBoncos && currentMarginNum < 0;
            
            const handleTanyaAsa = () => {
              openAsa('Database Brand (Manajemen Harga)', {
                brand: form.nama_brand,
                varian: form.nama_varian,
                ukuran_ml: form.botol_ukuran_label_ml,
                modal_fisik: modalFisikTotal,
                jual_standar: totalSetelahJasa,
                jasa_produksi: jasaFinal,
                margin_custom: currentMarginNum,
                total_profit: totalProfitBersih,
                harga_jual_akhir: currentHargaJualNum
              });
            };

            return (
              <div className={`border rounded-xl overflow-hidden transition-all ${
                isBoncos 
                  ? 'border-rose-300 bg-rose-50/40 shadow-xs' 
                  : isUnderSystem 
                  ? 'border-amber-300 bg-amber-50/30' 
                  : 'border-emerald-200 bg-emerald-50/30'
              }`}>
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white">
                  <div className="flex items-center gap-2">
                    <Banknote className={`w-5 h-5 ${isBoncos ? 'text-rose-600' : isUnderSystem ? 'text-amber-600' : 'text-emerald-700'}`} />
                    <h4 className={`text-xs font-black uppercase tracking-wider ${isBoncos ? 'text-rose-800' : isUnderSystem ? 'text-amber-800' : 'text-emerald-800'}`}>
                      Manajemen Harga & Jasa (Marketing)
                    </h4>
                  </div>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={handleTanyaAsa}
                    className="h-7 text-[10px] font-bold border-slate-200 text-slate-700 hover:bg-slate-50 gap-1.5 rounded-full px-3"
                  >
                    <Sparkles className="w-3 h-3 text-amber-500" /> Tanya Asa
                  </Button>
                </div>
                <div className="p-5 bg-white space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* 1. Harga Sistem Sebelum Jasa (Harga Dasar Sistem Bahan Baku) */}
                    <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm">
                      <label className="text-xs font-bold text-slate-500 mb-2 block">Harga Sistem (Sebelum Jasa)</label>
                      <div className="h-10 px-3 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-between">
                        <span className="text-lg font-black text-emerald-700">Rp {hargaSistemBahan.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded">Dasar Sistem</span>
                      </div>
                      
                      <div className="mt-2.5 pt-2 border-t border-slate-100 space-y-1.5 text-[10px]">
                        {/* Rincian Harga Jual Bahan */}
                        <div>
                          <span className="text-slate-400 font-medium block text-[9.5px] mb-0.5">Rincian Harga Jual Bahan:</span>
                          <p className="text-slate-600 flex flex-wrap gap-x-1.5 items-center leading-relaxed">
                            <span>Kemasan: <strong className="text-slate-800 font-bold">Rp {hppKemasanUtama.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                            <span className="text-slate-300">·</span>
                            <span>Cairan: <strong className="text-slate-800 font-bold">Rp {hppRacikanFinal.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                            {hppItemTambahan > 0 && (
                              <>
                                <span className="text-slate-300">·</span>
                                <span>Tambahan: <strong className="text-slate-800 font-bold">Rp {hppItemTambahan.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                              </>
                            )}
                          </p>
                        </div>

                        {/* Rincian Modal Beli Bahan */}
                        <div className="pt-1.5 border-t border-slate-100">
                          <div className="flex justify-between text-slate-500 mb-0.5">
                            <span className="text-[9.5px]">Total Modal Beli:</span>
                            <strong className="text-slate-700 font-bold">Rp {modalFisikTotal.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                          </div>
                          <p className="text-[9px] text-slate-400 flex flex-wrap gap-x-1.5">
                            <span>K: Rp {modalKemasanUtama.toLocaleString('id-ID')}</span>·
                            <span>C: Rp {modalRacikanFinal.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>·
                            <span>T: Rp {modalItemTambahan.toLocaleString('id-ID')}</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 2. Harga Jasa Produksi & Jumlah Setelah Jasa */}
                    <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm">
                      <label className="text-xs font-bold text-slate-500 mb-2 block">Harga Jasa Produksi</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 font-bold">Rp</span>
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={form.custom_jasa !== '' && form.custom_jasa !== null && form.custom_jasa !== undefined ? new Intl.NumberFormat('id-ID').format(form.custom_jasa) : ''}
                          onChange={e => {
                            const val = e.target.value.replace(/\D/g, '');
                            set('custom_jasa', val ? Number(val) : '');
                          }}
                          placeholder={String(defaultJasaVal)}
                          className="pl-9 h-10 text-sm font-bold border-slate-200 focus-visible:ring-emerald-500"
                        />
                      </div>
                      
                      {/* Rincian Jumlah Setelah Jasa */}
                      <div className="mt-2.5 pt-2 border-t border-slate-100 space-y-1 text-[10px]">
                        <span className="text-slate-400 font-medium block text-[9.5px]">Jumlah Setelah + Jasa:</span>
                        <div className="h-9 px-3 rounded-md bg-emerald-50/70 border border-emerald-200 flex items-center justify-between">
                          <span className="text-sm font-black text-emerald-800">
                            Rp {totalSetelahJasa.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <span className="text-[9px] bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded">
                            Bahan + Jasa
                          </span>
                        </div>
                        <p className="text-[9px] text-slate-400 italic">
                          {form.custom_jasa !== '' ? 'Harga jasa custom' : `Otomatis: Rp ${defaultJasaVal.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${ukuranMl}ml)`}
                        </p>
                      </div>
                    </div>

                    {/* 3. Keuntungan / Margin */}
                    <div className={`bg-white border p-4 rounded-xl shadow-sm transition-all ${
                      isBoncos 
                        ? 'border-rose-300 bg-rose-50/20' 
                        : isUnderSystem 
                        ? 'border-amber-300 bg-amber-50/20' 
                        : 'border-emerald-200'
                    }`}>
                      <label className="text-xs font-bold text-slate-500 mb-2 block flex justify-between items-center">
                        <span>Margin Tambahan (Rp)</span>
                        <span className={`text-[9px] font-bold ${
                          isBoncos ? 'text-rose-600' : isUnderSystem ? 'text-amber-600' : 'text-emerald-600'
                        }`}>
                          {isBoncos ? 'Di Bawah Modal' : isUnderSystem ? 'Di Bawah Rekomendasi' : 'Otomatis / Manual'}
                        </span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">Rp</span>
                        <Input 
                          type="text"
                          inputMode="numeric"
                          className={`pl-9 h-10 text-sm font-bold ${
                            isBoncos 
                              ? 'border-rose-300 bg-rose-50/40 text-rose-900 focus-visible:ring-rose-500' 
                              : isUnderSystem 
                              ? 'border-amber-300 bg-amber-50/30 text-amber-900 focus-visible:ring-amber-500' 
                              : 'border-emerald-200 bg-emerald-50/20 text-emerald-900 focus-visible:ring-emerald-500'
                          }`}
                          placeholder="0"
                          value={displayMargin !== '' ? (displayMargin === 0 ? '0' : (displayMargin < 0 ? `- ${new Intl.NumberFormat('id-ID').format(Math.abs(Math.round(displayMargin)))}` : new Intl.NumberFormat('id-ID').format(Math.round(displayMargin)))) : ''}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9-]/g, '');
                            if (!raw || raw === '-') {
                              set('custom_margin', '');
                              set('custom_harga_jual', '');
                            } else {
                              const m = Number(raw);
                              set('custom_margin', m);
                              set('custom_harga_jual', Math.round(totalSetelahJasa + m));
                            }
                          }}
                        />
                      </div>
                      <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] space-y-0.5 text-slate-500">
                        <div className="flex justify-between text-[9.5px]">
                          <span>Margin Kemasan:</span>
                          <span>+ Rp {marginKemasan.toLocaleString('id-ID')}</span>
                        </div>
                        <div className="flex justify-between text-[9.5px]">
                          <span>Margin Cairan:</span>
                          <span>+ Rp {marginCairan.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className={`flex justify-between font-bold pt-1 border-t ${
                          isBoncos ? 'border-rose-200 text-rose-700' : isUnderSystem ? 'border-amber-200 text-amber-700' : 'border-emerald-100 text-emerald-700'
                        }`}>
                          <span>Total Keuntungan Bersih:</span>
                          <span>
                            {totalProfitBersih < 0 ? `- Rp ${Math.abs(totalProfitBersih).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `Rp ${totalProfitBersih.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Harga Jual Akhir (Span 3 Columns on larger screen for prominence) */}
                    <div className={`p-4 rounded-xl shadow-sm md:col-span-3 border-2 transition-all ${
                      isBoncos 
                        ? 'bg-rose-50/60 border-rose-400' 
                        : isUnderSystem 
                        ? 'bg-amber-50/50 border-amber-400' 
                        : 'bg-gradient-to-r from-emerald-50/40 via-white to-emerald-50/40 border-emerald-300'
                    }`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <label className={`text-sm font-black block flex items-center gap-2 ${
                            isBoncos ? 'text-rose-900' : isUnderSystem ? 'text-amber-900' : 'text-emerald-900'
                          }`}>
                            Harga Jual Akhir (Ditagihkan ke Klien)
                            {isBoncos ? (
                              <span className="text-[10px] bg-rose-600 text-white px-2 py-0.5 rounded-full font-bold tracking-tight">
                                ● Defisit Modal Fisik
                              </span>
                            ) : isUnderSystem ? (
                              <span className="text-[10px] bg-amber-600 text-white px-2 py-0.5 rounded-full font-bold tracking-tight">
                                ● Di Bawah Estimasi Sistem
                              </span>
                            ) : (
                              <span className="text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded-full font-bold tracking-tight">
                                ● Margin Terkunci
                              </span>
                            )}
                          </label>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            {isBoncos ? (
                              <strong className="text-rose-700 font-semibold">
                                Estimasi harga jual berada di bawah modal pokok fisik (Rp {modalFisikTotal.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}). Terdapat potensi defisit Rp {Math.abs(totalProfitBersih).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per pcs.
                              </strong>
                            ) : isUnderSystem ? (
                              <strong className="text-amber-700 font-semibold">
                                Harga disesuaikan di bawah estimasi standar sistem (Rp {totalSetelahJasa.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}), proyeksi margin bersih Rp {totalProfitBersih.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per pcs.
                              </strong>
                            ) : (
                              'Harga jual yang disepakati dengan klien. Margin akan otomatis terkunci untuk Repeat Order agar proyeksi biaya tetap stabil.'
                            )}
                          </p>
                        </div>
                        <div className="relative w-full sm:w-64 shrink-0">
                          <span className={`absolute left-3.5 top-1/2 -translate-y-1/2 font-black text-sm ${
                            isBoncos ? 'text-rose-700' : isUnderSystem ? 'text-amber-700' : 'text-emerald-700'
                          }`}>Rp</span>
                          <Input 
                            type="text"
                            inputMode="numeric"
                            className={`pl-10 h-11 text-base font-black border-2 rounded-xl bg-white shadow-xs ${
                              isBoncos 
                                ? 'border-rose-500 text-rose-900 focus-visible:ring-rose-500' 
                                : isUnderSystem 
                                ? 'border-amber-500 text-amber-900 focus-visible:ring-amber-500' 
                                : 'border-emerald-500 text-emerald-900 focus-visible:ring-emerald-500'
                            }`}
                            placeholder={new Intl.NumberFormat('id-ID').format(Math.round(totalSetelahJasa))}
                            value={displayHargaJual !== '' ? new Intl.NumberFormat('id-ID').format(Math.round(displayHargaJual)) : ''}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/\D/g, '');
                              if (!raw) {
                                set('custom_harga_jual', '');
                                set('custom_margin', '');
                              } else {
                                const numVal = Number(raw);
                                set('custom_harga_jual', numVal);
                                const newMargin = numVal - totalSetelahJasa;
                                set('custom_margin', newMargin);
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
          </>
          ) : (
            <div className="space-y-4 pt-4 border-t border-slate-200">
              <h4 className="text-sm font-black text-[#8C6208] flex items-center gap-2 uppercase tracking-wider mb-2">
                <Package className="w-4 h-4" /> Daftar Isi Discovery Set
              </h4>
              <DiscoverySetItems 
                items={discoverySetItems} 
                onChange={setDiscoverySetItems}
                onItemRemove={handleRemoveDSItem}
                botolList={botolList}
                tutupList={tutupList}
                sprayList={sprayList}
                fullsetList={fullsetList}
                bahanList={bahanCairList}
                isMarketing={isMarketing}
              />
              {/* Item Tambahan (Khusus Keseluruhan Discovery Set) */}
              <div className="border border-slate-200 rounded-xl p-4 mt-6 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h5 className="text-xs font-bold text-slate-700">Item Tambahan Kemasan</h5>
                    <p className="text-[10px] text-slate-500">Box, Stiker, dsb. yang berlaku untuk seluruh set (hanya dihitung 1x di HPP).</p>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addItemTambahan} className="h-6 text-[10px] gap-1 px-2 rounded">
                    <Plus className="w-3 h-3" /> Tambah
                  </Button>
                </div>
                {form.item_tambahan.length === 0 ? (
                  <p className="text-[10px] text-slate-400 italic text-center py-2">Tidak ada (Box, stiker, dsb)</p>
                ) : (
                  <div className="space-y-3">
                    {form.item_tambahan.map((it, i) => (
                      <div key={i} className="bg-slate-50 rounded-xl border border-slate-200 p-3 relative">
                        <Button type="button" variant="ghost" size="icon" className="absolute top-2 right-2 w-6 h-6 text-rose-500/50 hover:text-rose-600 hover:bg-rose-50" onClick={() => removeItemTambahan(i)}><Trash2 className="w-3 h-3" /></Button>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] font-bold text-slate-500 uppercase">Item {i + 1}</span>
                          <button type="button" onClick={() => updateItemTambahan(i, 'dari_client', !it.dari_client)} className={`text-[9px] px-2 py-0.5 rounded-full font-bold transition-all ${it.dari_client ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-slate-200 text-slate-500 hover:bg-slate-300'}`}>
                            {it.dari_client ? '✓ Dari Client' : 'Milik Sendiri / Beli'}
                          </button>
                        </div>
                        <div className="mb-2">
                          <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Kategori Item</label>
                          <div className="flex gap-1.5 flex-wrap">
                            {ITEM_TAMBAHAN_KATEGORI.map(k => {
                               const activeClass = k === 'Stiker' ? 'bg-blue-500 text-white' : 
                                                   k === 'Box' ? 'bg-orange-500 text-white' :
                                                   k === 'Sablon' ? 'bg-purple-500 text-white' :
                                                   k === 'Hot Print' ? 'bg-rose-500 text-white' :
                                                   k === 'Gift Card' ? 'bg-pink-500 text-white' :
                                                   k === 'Coloring' ? 'bg-teal-500 text-white' :
                                                   'bg-slate-700 text-white';
                               return (
                                <button key={k} type="button" onClick={() => { updateItemTambahan(i, 'kategori', k); updateItemTambahan(i, 'nama', ''); }}
                                  className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all ${(it.kategori || 'Stiker') === k ? `${activeClass} shadow-sm` : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                                  {k}
                                </button>
                               );
                            })}
                          </div>
                        </div>
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-12 sm:col-span-6">
                            <SearchableCombobox
                              value={it.nama}
                              onValueChange={v => {
                                updateItemTambahan(i, 'nama', v);
                                const found = itemTambahanList.find(x => x.nama === v);
                                if (found && found.kategori) updateItemTambahan(i, 'kategori', found.kategori);
                              }}
                              options={itemTambahanList
                                .filter(x => (it.kategori ? x.kategori === it.kategori : true))
                                .map(x => ({ value: x.nama, label: x.nama }))}
                              placeholder="Nama Item..."
                              size="sm"
                            />
                          </div>
                          <div className="col-span-5 sm:col-span-3">
                            <div className="flex items-center gap-2">
                              <label className="text-[9px] font-bold text-slate-400 uppercase">Qty:</label>
                              <Input type="number" min="1" value={it.qty_per_pcs} onChange={e => updateItemTambahan(i, 'qty_per_pcs', Number(e.target.value))} className="h-8 text-[10px] text-center px-1" />
                            </div>
                          </div>
                          <div className="col-span-7 sm:col-span-3">
                            <Input value={it.catatan || ''} onChange={e => updateItemTambahan(i, 'catatan', e.target.value)} placeholder="Ket..." className="h-8 text-[10px] px-2" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SECTION 4: Lainnya (Artwork, PIC, Catatan) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-200">
            {/* Upload Artwork */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-700 block flex items-center justify-between">
                Artwork / Desain
                {form.image_url && <button type="button" onClick={() => set('image_url', '')} className="text-[10px] text-rose-500 hover:underline">Hapus</button>}
              </label>
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-2 bg-slate-50">
                {form.image_url ? (
                  <img src={form.image_url} alt="Artwork" className="max-h-32 mx-auto object-contain rounded-lg" />
                ) : (
                  <div className="py-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-slate-100 rounded-lg transition-colors" onClick={() => fileRef.current?.click()}>
                    <Upload className="w-6 h-6 text-slate-400" />
                    <p className="text-[10px] text-slate-500 text-center font-medium px-4">Unggah mockup/desain botol kemasan (Opsional)</p>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUploadImage} />
              </div>
            </div>

            <div className="space-y-4">
              <PicSelector label="Formulator / PIC" value={form.pic} onChange={v => set('pic', v)} requiredPageAccessKey="database_brand" />
              
              <div>
                <label className="text-xs font-bold text-slate-700 mb-1 block">Catatan Umum</label>
                <Input value={form.catatan} onChange={e => set('catatan', e.target.value)} placeholder="Catatan internal..." className="h-10 text-xs" />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 mb-1 block">Status Aktif</label>
                <div className="flex bg-slate-100 rounded-lg p-1">
                  <button type="button" onClick={() => set('status', 'aktif')} className={`flex-1 text-xs font-bold py-1.5 rounded-md transition-all ${form.status === 'aktif' ? 'bg-white shadow-sm text-emerald-600' : 'text-slate-500'}`}>Aktif</button>
                  <button type="button" onClick={() => set('status', 'arsip')} className={`flex-1 text-xs font-bold py-1.5 rounded-md transition-all ${form.status === 'arsip' ? 'bg-white shadow-sm text-rose-600' : 'text-slate-500'}`}>Arsip</button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-6 border-t border-slate-200">
            <Button type="button" variant="ghost" onClick={onClose} className="flex-1 rounded-xl h-12 font-bold text-slate-500 bg-slate-50 hover:bg-slate-100">Batal</Button>
            <Button type="submit" disabled={saveMut.isPending} className={`flex-1 rounded-xl h-12 font-bold gap-2 text-white shadow-md ${isFormValid ? 'bg-[#302621] hover:bg-black' : 'bg-slate-300'}`}>
              {saveMut.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
              {saveMut.isPending ? 'Menyimpan Database...' : isEdit ? 'Simpan Perubahan' : 'Simpan Produk Baru'}
            </Button>
          </div>

        </form>
      </div>
    </div>
  );
}
